import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { CommitOnEnterDirective } from 'src/app/shared/directives/commit-on-enter.directive';
import { SelectOnFocusDirective } from 'src/app/shared/directives/select-on-focus.directive';
import { TwoDecimalsDirective } from 'src/app/shared/directives/two-decimals.directive';
import { US_STATES } from 'src/app/shared/constants/us-states.constant';
import {
  ReallocationV2Store,
  NEW_RECIPIENT_PAYEE_REQUIRED,
} from '../../state/reallocation-v2.store';

/**
 * Step 1 of a Contribution or Distribution — the amount, and how the money travels.
 *
 * Shared by both types, which is what the V1 workflows also settled on (`app-delivery-methods`
 * serves contribution, distribution and close account alike). The differences are real but
 * narrow: only a distribution withholds tax, offers a gross/net toggle, or can close the
 * account, and only a distribution is capped by what the account holds.
 *
 * ⚠️ Two selects here are driven by `req.DeliveryMethod` / `req.DeliveryFrequency` rather than
 * by a front-end enum, because their ids are NOT NULL foreign keys on the request. Until the
 * FIG Data package carrying that lookup is published they render empty and the step's gate
 * refuses to advance — which is the intended failure, not a bug to route around.
 */
@Component({
  selector: 'app-rav2-funding-step',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, MatDatepickerModule, MatNativeDateModule,
    SelectOnFocusDirective, CommitOnEnterDirective, TwoDecimalsDirective,
  ],
  templateUrl: './funding-step.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FundingStepComponent {
  readonly store = inject(ReallocationV2Store);

  /** The state dropdown's options — the 50 states plus DC, ordered by name. */
  readonly states = US_STATES;

  /**
   * What a cheque to an address Schwab does not hold obliges instead.
   *
   * ⚠️ Still a constant, unlike the instruction sentence beside it: only one method reads the
   * address book, so there is no name to vary and nothing for the store to decide. The document
   * note moved to `store.newRecipientInstructionNote` because its subject is the chosen method.
   */
  readonly payeeNote = NEW_RECIPIENT_PAYEE_REQUIRED;

  /**
   * Why the address list has only one row.
   *
   * ⚠️ A STATEMENT, and deliberately not the custodial-paperwork sentence the bank list shows.
   * An account with no addresses on file is not a problem the advisor has to go and solve —
   * typing the payee is a complete, ordinary answer for a cheque, and the panel beneath this is
   * already open for them to do it. Sending them to Schwab Advisor Center would be sending them
   * nowhere.
   */
  readonly noAddressesNote = 'No addresses on record for this account — enter the payee details '
    + 'below.';

  /**
   * The LEFT COLUMN's heading, paired with 'Delivery' on the right.
   *
   * ⚠️ 'Amount', NOT 'Funding' (AndyP, 2026-09-30). This read 'Funding' from 2026-09-22 on the
   * reasoning that the amount is only part of what the column holds — it also carries the
   * gross/net choice and the tax withholdings — so naming it after one field made the column look
   * narrower than it is. That was overturned: 'Funding' restates the step's own title without
   * telling the advisor what to do here, where 'Amount' names the decision the column asks for.
   * The withholdings hang off that decision rather than competing with it.
   *
   * 🟢 The step title above still says which request this is ('Distribution Amount'), so the type
   * is not lost — the column just does not repeat it a line below.
   *
   * ⚠️ THIS PAIR IS LOAD-BEARING, and that is the settled design rather than a side effect
   * (AndyP, 2026-10-02, checked against the rendered step). The title used to read 'Distribution
   * Amount and Delivery' and named the right column on the left column's behalf; it was shortened
   * to match the contribution, which removed that backstop deliberately.
   *
   * Delivery stays on this step and is named NOWHERE ELSE -- not in the step title, which this
   * step suppresses, and not in the stepper chip, which reads 'Distribution Amount'. Giving it a
   * step of its own was considered and rejected: the two decisions are independent and neither
   * constrains the other, so splitting them would add a step to make room for a word.
   *
   * 🔴 So 'Amount' here and 'Delivery' opposite are the ONLY things captioning the two halves.
   * Dropping either as redundant leaves that half unlabelled -- which is the exact state the long
   * title was invented to prevent, arrived at from the other direction.
   */
  heading(): string {
    if (this.store.isCloseAccount()) return 'Close the Account';
    return 'Amount';
  }

  amountLabel(): string {
    if (this.store.isContribution()) return 'How much would you like to contribute?';
    return this.store.isNetEntry()
      ? 'Enter NET amount (what the client receives).'
      : 'Enter GROSS amount (what leaves the account).';
  }

  /** "contribution" / "distribution", for the delivery card's labels. */
  requestNoun(): string {
    return this.store.isContribution() ? 'contribution' : 'distribution';
  }

  /**
   * Why the method cannot be changed — V1's wording, verbatim.
   *
   * Only ever shown when {@link ReallocationV2Store.deliveryMethodLocked} is true, which is a
   * property of the request type rather than of the method, so the sentence names the support
   * limitation rather than anything about the account.
   */
  lockedMethodReason(): string {
    return 'Only ACH is supported at this time';
  }

  /** Why the frequency cannot be changed. Reads as a fact about closes, not as a restriction. */
  lockedFrequencyReason(): string {
    return 'A close account distribution is paid out once';
  }

  setEntryMode(mode: 'gross' | 'net'): void {
    this.store.fundingForm.controls.entryMode.setValue(mode);
  }

  /**
   * Pick a delivery method by id.
   *
   * The id comes off the option rather than the select's string value being parsed loosely:
   * `<select>` values are strings, and an unparsed one would be stored as `"2"` and never match
   * the numeric id the lookup and the payload use.
   */
  onMethod(event: Event): void {
    const raw = (event.target as HTMLSelectElement).value;
    const id = raw === '' ? null : Number(raw);
    this.store.fundingForm.controls.deliveryMethodId.setValue(Number.isFinite(id as number) ? id : null);
    // The bank list is a property of the method, so a method change can invalidate the chosen
    // account. Cleared rather than left dangling: a stale key would render as nothing selected
    // while the form still held it, and the gate would pass on an instruction nobody picked.
    this.store.fundingForm.controls.standingInstructionKey.setValue(null);
    // Overnight delivery only exists for a check. Switching away from one has to clear it, or a
    // request that was briefly a check would file an ACH transfer flagged for overnight
    // delivery — a contradiction the control is no longer on screen to explain.
    if (!this.store.allowsOvernightDelivery()) {
      this.store.fundingForm.controls.isOvernightDelivery.setValue(false);
    }
    // 🔴 FORGETS THE PAYEE, and fills in the lone address where that is all there is. A payee
    // typed against Check survives a switch to ACH otherwise and reaches the payload: a posting
    // address filed against a transfer with no cheque to post, which the trade team would read
    // as an instruction.
    //
    // Runs on EVERY method change, not only on the way out of Check. The obligations differ by
    // method -- one wants an address typed here, another a document attached two steps later --
    // so carrying half-collected details across a switch attaches them to the wrong one.
    this.store.resetRecipientForMethod();
  }

  /**
   * Pick where a cheque is posted.
   *
   * Shares `standingInstructionKey` with the bank-account select, and that is deliberate: only
   * one of the two is ever on screen, both answer *where does this money land*, and the chosen
   * row's `displayValue` is what reaches `DeliveryInstruction` either way. A second control would
   * be a second thing to clear on every method change.
   *
   * ⚠️ Choosing an address ON FILE clears the payee block. An advisor who types an address, then
   * reconsiders and picks one Schwab already holds, must not file both -- the typed one would
   * reach `req.AccountRequestPayee` and contradict the instruction beside it.
   */
  onAddress(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.store.fundingForm.controls.standingInstructionKey.setValue(value === '' ? null : value);
    if (!this.store.payeeRequired()) {
      this.store.fundingForm.controls.payee.reset();
    }
  }

  /**
   * Whether one named payee field is still blank.
   *
   * Reads the store's one list rather than re-testing each control, so the markers on screen and
   * the count in the Submit blocker can never disagree about what is outstanding.
   */
  payeeMissing(field: string): boolean {
    return this.store.payeeMissingFields().includes(field);
  }

  /**
   * What the radios are a type OF — the noun follows the control above them.
   *
   * ⚠️ One question, two nouns. *Whose is it* is the same question whether the destination is a
   * bank account or a mailing address, and the lookup is shared — but 'Address type' sitting over
   * a bank account reads as a mistake, and a reader who notices it stops trusting the panel.
   *
   * Falls back to the address wording, which is where the question started and the only case a
   * method with neither flag could reach — and no such method asks.
   */
  recipientTypeLabel(): string {
    return this.store.usesBankAccount() ? 'Account type' : 'Address type';
  }

  /**
   * Record who the money is going to.
   *
   * ⚠️ `(change)` with `[checked]`, not `formControlName`. The control sits on the form ROOT
   * while these radios render inside the payee group's `[formGroup]` scope, and a
   * `formControlName` there would resolve against the group and throw at runtime. The same
   * reason the two selects on this step are bound by hand.
   */
  onRecipientType(id: number): void {
    this.store.fundingForm.controls.deliveryRecipientTypeId.setValue(id);
  }

  onFrequency(event: Event): void {
    const raw = (event.target as HTMLSelectElement).value;
    const id = raw === '' ? null : Number(raw);
    this.store.fundingForm.controls.deliveryFrequencyId.setValue(Number.isFinite(id as number) ? id : null);
    // A one-time transfer has no schedule, so any date already picked is now meaningless.
    // Cleared rather than hidden-and-kept, or an advisor who switched to One Time would submit
    // a start date the request cannot honour.
    if (!this.store.startDateRequired()) {
      this.store.fundingForm.controls.startDate.setValue(null);
    }
  }

  onStandingInstruction(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.store.fundingForm.controls.standingInstructionKey.setValue(value === '' ? null : value);
  }

  onAmount(event: Event): void {
    const raw = (event.target as HTMLInputElement).value.replace(/,/g, '');
    const parsed = parseFloat(raw);
    this.store.fundingForm.controls.amount.setValue(raw === '' || isNaN(parsed) ? null : parsed);
  }

  onFederalTax(event: Event): void {
    this.store.fundingForm.controls.federalTaxPct.setValue(this.percent(event));
  }

  onStateTax(event: Event): void {
    this.store.fundingForm.controls.stateTaxPct.setValue(this.percent(event));
  }

  private percent(event: Event): number | null {
    const raw = (event.target as HTMLInputElement).value.replace(/[,%]/g, '');
    const parsed = parseFloat(raw);
    if (raw === '' || isNaN(parsed)) return null;
    // Clamped to a percentage. Above 100 the net-to-gross conversion inverts, which would file a
    // distribution that pays money into the account.
    return Math.min(100, Math.max(0, parsed));
  }

  /**
   * The current withholding on file, for the CURRENT caption.
   *
   * '—' rather than '0.00%' when Schwab did not say. Zero is a real election an advisor may be
   * deliberately preserving; not knowing is a different thing, and printing 0.00% would invite
   * them to "keep" an election nobody has established.
   */
  currentTaxCaption(value: number | null | undefined): string {
    return value == null ? '—' : `${value.toFixed(2)}%`;
  }

  /**
   * True when the chosen bank authorization does not carry advisory authority.
   *
   * The condition itself is {@link ReallocationV2Store.noIaAuthority} — including the guard that
   * it only applies to a method with a bank authorization in the first place, which this method
   * was missing while Review & Submit's row was missing it too.
   */
  showNoAuthorityWarning(): boolean {
    return this.store.noIaAuthority();
  }

  /** True when a method is chosen and the account has no bank authorization that can carry it. */
  showNoInstructionsWarning(): boolean {
    const failed = this.store.funding()?.standingInstructionsFailed === true;
    return !failed
      // 🔴 BANK ACCOUNTS ONLY, and never the address book. The sentence tells the advisor to file
      // custodial paperwork, which is the right next move for a transfer the account is not
      // authorized to make -- and the wrong one entirely for a cheque, where typing the payee is
      // a complete, ordinary answer. `addressBookEmpty` carries a plain note instead.
      //
      // It is also why Leave in Cash and Move to Retail are excluded: they have no account to
      // send to, so "no standing authorization" would be a non-sequitur.
      && this.store.usesBankAccount()
      // 1 rather than 0: the synthetic "Other (New Account)" entry is always present, so an
      // account with nothing on file still has one option.
      && this.store.bankAccountOptions().length <= 1;
  }
}
